import { Accordion } from '@liveauctioneers/hammer-ui-core/accordion';
import { Checkbox } from '@liveauctioneers/hammer-ui-core/checkbox';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { CurrentOpenMenu, MAX_FILTER_OPTIONS_COUNT } from '@/pages/SearchPageSimplified/types';
import { FacetOption } from '@/types/FacetOption';
import { FilterAction } from '@/types/SearchFacet';
import { getMergedOptionFacet } from '@/redux/modules/searchFacetAggregates.selectors';
import { getRoundedNumberText } from '@/pages/SearchPageSimplified/utils/getRoundedNumberText';
import { H5 } from '@liveauctioneers/hammer-ui-core/text';
import { modifyParamsRemovePage, modifyParamsWhenChecked, parseQueryParams } from '@/utils/queryParams';
import { SearchInput } from '@liveauctioneers/hammer-ui-core/searchInput';
import { useAppSelector } from '@/redux/hooks';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useLocation } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useNavigateSearchPageWithParams } from '@/pages/SearchPageSimplified/hooks/useNavigateSearchPageWithParams';
import { ViewMore } from '@liveauctioneers/hammer-ui-core/viewMore';
import FacetTypes from '@/types/search/enums/facetTypes';

const MAX_ITEMS_BEFORE_SCROLLING = 4;

type Props = {
    isArchivedSearch: boolean;
};

export const AuctionHouseFilter = ({ isArchivedSearch }: Props) => {
    const { formatMessage } = useIntl();

    const [isExpanded, setIsExpanded] = useState(false);
    const [auctionHouseSearchText, setAuctionHouseSearchText] = useState('');

    const { search } = useLocation();
    const queryParams = parseQueryParams(search);
    const navigateWithParams = useNavigateSearchPageWithParams();
    const auctionHouseFacet = useAppSelector((state) =>
        getMergedOptionFacet(state, isArchivedSearch, FacetTypes.AUCTION_HOUSE_ID)
    );
    const { options = [] } = auctionHouseFacet;

    const onAuctionHouseOptionClick = (option: FacetOption) => {
        const newCheckStatus = !option.selected;

        let newParams = modifyParamsWhenChecked(queryParams, FacetTypes.AUCTION_HOUSE_ID, option.id, newCheckStatus);

        newParams = modifyParamsRemovePage(newParams);

        navigateWithParams({
            currentOpenMenu: CurrentOpenMenu.AllFilters,
            filterAction: newCheckStatus ? FilterAction.AuctionHouseChanged : FilterAction.AuctionHouseRemoved,
            params: newParams,
            preventScrollReset: true,
        });
    };

    const filteredOptions = useMemo(() => {
        let optionsCopy = options;

        if (auctionHouseSearchText.length) {
            optionsCopy = options.filter((option) => {
                // option.name might be undefined, so we need to check for that
                return option.name?.toLowerCase().includes(auctionHouseSearchText.toLowerCase());
            });
        }

        const sortedOptions = optionsCopy
            .filter((option) => option.count !== 0 || option.selected)
            .sort((a, b) => b.count - a.count);

        if (!isExpanded) {
            return sortedOptions.slice(0, MAX_FILTER_OPTIONS_COUNT);
        }
        return sortedOptions;
    }, [options, auctionHouseSearchText, isExpanded]);

    if (filteredOptions.length === 0 && auctionHouseSearchText === '') {
        return null;
    }

    return (
        <Accordion
            className="w-full"
            density="compact"
            emphasis="subtle"
            headline={formatMessage({ id: 'search_facet_title_auctionHouse' })}
            open
        >
            <ViewMore
                collapseText={formatMessage({ id: 'view_less' })}
                expandText={formatMessage({ id: 'view_more' })}
                fadeBackgroundColor="#fff"
                onChange={(isExpanded) => setIsExpanded(isExpanded)}
                textCutoffHeight={230}
            >
                <div className="flex flex-col gap-xs">
                    <SearchInput
                        className="mb-xs"
                        onClear={() => setAuctionHouseSearchText('')}
                        onTextChange={(searchText: string) => setAuctionHouseSearchText(searchText)}
                        placeholder={formatMessage({ id: 'searchPage.facets.filterActionHouses' })}
                        small
                    />
                    {filteredOptions.length > 0 ? (
                        <div
                            className={cn('flex max-h-[400px] flex-col gap-xs py-xs', {
                                'overflow-y-auto': isExpanded,
                                'overflow-y-hidden': !isExpanded && filteredOptions.length > MAX_ITEMS_BEFORE_SCROLLING,
                            })}
                        >
                            {filteredOptions.map((option) => (
                                <Checkbox.Option
                                    checked={option.selected}
                                    disabled={option.count === 0}
                                    key={option.id}
                                    onChange={() => onAuctionHouseOptionClick(option)}
                                >
                                    {option.name} ({getRoundedNumberText({ totalFound: option.count })})
                                </Checkbox.Option>
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col gap-xs">
                            <H5>{formatMessage({ id: 'searchPage.facets.noResultsFound' })}</H5>
                        </div>
                    )}
                </div>
            </ViewMore>
        </Accordion>
    );
};
